import { SaveFiltersButton } from '@/components/transactions/SaveFiltersButton';
import { Badge } from '@/components/ui/badge';
import useAppwriteTableQuery from '@/hooks/use-appwrite-table';
import useCategories from '@/hooks/use-categories';
import {
  filterActions,
  filterStore,
  useTransactionFilters,
} from '@/state/transactions-filter-state';
import { APP_COLLECTIONS, UNCATEGORIZED_FILTER } from '@polybank/shared';
import dayjs from 'dayjs';
import { memo, useMemo } from 'react';
import { useSnapshot } from 'valtio';

export const ActiveFiltersChips = memo(function ActiveFiltersChips() {
  // Get filter state from store
  const filterSnap = useSnapshot(filterStore);

  const { mappedRows: bankAccountsMap } = useAppwriteTableQuery(
    APP_COLLECTIONS.BANK_ACCOUNTS
  );

  // const [ruleDialogOpen, setRuleDialogOpen] = useState(false);
  const { categoriesMap } = useCategories();
  const activeFilters = useTransactionFilters();

  const dateRange = useMemo(() => {
    const from = filterSnap.appliedFilters.dateRange?.from
      ? dayjs(filterSnap.appliedFilters.dateRange?.from).format('MMM D, YYYY')
      : 'Today';
    const to = filterSnap.appliedFilters.dateRange?.to
      ? dayjs(filterSnap.appliedFilters.dateRange?.to).format('MMM D, YYYY')
      : 'Today';

    if (from === to) {
      return from;
    }
    return `${from} - ${to}`;
  }, [filterSnap.appliedFilters.dateRange]);

  const shouldShowChips = useMemo(() => {
    return Object.keys(activeFilters.appliedFilters)
      .filter((key) => key !== 'searchMode')
      .some((key) => {
        return activeFilters.appliedFilters[
          key as keyof typeof activeFilters.appliedFilters
        ];
      });
  }, [activeFilters]);

  return (
    <div className='flex flex-wrap items-center gap-2'>
      {shouldShowChips && (
        <>
          <span className='text-sm text-muted-foreground'>Active filters:</span>
          <SaveFiltersButton />
        </>
      )}
      {filterSnap.appliedFilters.searchQuery && (
        <Badge variant='secondary' className='gap-1'>
          Search: {filterSnap.appliedFilters.searchQuery}
          <button
            onClick={() => {
              filterActions.removeAndApplyFilter('searchQuery');
            }}
            className='hover:bg-destructive/20 rounded-full p-0.5'
          >
            ×
          </button>
        </Badge>
      )}
      {activeFilters.appliedFilters.accountFilter?.map((accountFilter) => (
        <Badge key={accountFilter} variant='secondary' className='gap-1'>
          Account: {bankAccountsMap[accountFilter]?.account_name}
          <button
            onClick={() => {
              filterActions.updateFilter(
                'accountFilter',
                activeFilters.appliedFilters.accountFilter?.filter(
                  (c) => c !== accountFilter
                )
              );
              filterActions.applyFilters();
            }}
            className='hover:bg-destructive/20 rounded-full p-0.5'
          >
            ×
          </button>
        </Badge>
      ))}
      {filterSnap.appliedFilters.categories &&
        filterSnap.appliedFilters.categories.map((category) => (
          <Badge key={category} variant='secondary' className='gap-1'>
            Category:{' '}
            {category === UNCATEGORIZED_FILTER
              ? 'Uncategorized'
              : categoriesMap[category]?.name}
            <button
              onClick={() => {
                const newCategories =
                  filterSnap.appliedFilters.categories?.filter(
                    (c) => c !== category
                  ) || [];
                filterActions.updateFilter(
                  'categories',
                  newCategories.length > 0 ? newCategories : undefined,
                  true
                );
              }}
              className='hover:bg-destructive/20 rounded-full p-0.5'
            >
              ×
            </button>
          </Badge>
        ))}
      {filterSnap.appliedFilters.merchants &&
        filterSnap.appliedFilters.merchants.map((merchant) => (
          <Badge key={merchant} variant='secondary' className='gap-1'>
            Merchant: {merchant}
            <button
              onClick={() => {
                const newMerchants =
                  filterSnap.appliedFilters.merchants?.filter(
                    (m) => m !== merchant
                  ) || [];
                filterActions.updateFilter(
                  'merchants',
                  newMerchants.length > 0 ? newMerchants : undefined,
                  true
                );
              }}
              className='hover:bg-destructive/20 rounded-full p-0.5'
            >
              ×
            </button>
          </Badge>
        ))}
      {filterSnap.appliedFilters.transactionTypes &&
        filterSnap.appliedFilters.transactionTypes.length > 0 && (
          <Badge variant='secondary' className='gap-1'>
            Type: {filterSnap.appliedFilters.transactionTypes.join(', ')}
            <button
              onClick={() => {
                filterActions.removeAndApplyFilter('transactionTypes');
              }}
              className='hover:bg-destructive/20 rounded-full p-0.5'
            >
              ×
            </button>
          </Badge>
        )}
      {(filterSnap.appliedFilters.amountRange?.min != null ||
        filterSnap.appliedFilters.amountRange?.max != null) && (
        <Badge variant='secondary' className='gap-1'>
          Amount:{' '}
          {filterSnap.appliedFilters.amountRange?.min != null &&
          filterSnap.appliedFilters.amountRange?.max != null
            ? `${filterSnap.appliedFilters.amountRange.min} - ${filterSnap.appliedFilters.amountRange.max}`
            : filterSnap.appliedFilters.amountRange?.min != null
              ? `≥ ${filterSnap.appliedFilters.amountRange.min}`
              : `≤ ${filterSnap.appliedFilters.amountRange!.max}`}
          <button
            onClick={() => {
              filterActions.removeAndApplyFilter('amountRange');
            }}
            className='hover:bg-destructive/20 rounded-full p-0.5'
          >
            ×
          </button>
        </Badge>
      )}
      {(filterSnap.appliedFilters.dateRange?.from ||
        filterSnap.appliedFilters.dateRange?.to) && (
        <Badge variant='secondary' className='gap-1'>
          Date range: {dateRange}
          <button
            onClick={() => {
              filterActions.removeAndApplyFilter('dateRange');
            }}
            className='hover:bg-destructive/20 rounded-full p-0.5'
          >
            ×
          </button>
        </Badge>
      )}
      {/* <CreateRuleDialog
        open={ruleDialogOpen}
        onOpenChange={setRuleDialogOpen}
        presets={rulePresets}
      /> */}
    </div>
  );
});
